import { AnimatePresence, motion } from "motion/react"
import { HugeiconsIcon, Icons } from "@/components/icons"
import { ImgLogo, ImgLogoWithFullName } from "@/assets/images"
import { Link, useLocation } from "react-router-dom"
import {
  Sidebar as ShadcnSidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"

import { APP_ROUTES } from "@/constants/routes"
import { NAV_ITEMS } from "./nav-config"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import { useIntl } from "react-intl"

/* Ease-out curve for soft settle at end of collapse */
const labelTransition = { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const }
const logoTransition = { duration: 0.35, ease: [0.25, 0.1, 0.25, 1] as const }

interface AppSidebarContentProps {
  logoAlt: string
}

export function AppSidebarContent({ logoAlt }: AppSidebarContentProps) {
  const intl = useIntl()
  const pathname = useLocation().pathname
  const { setOpenMobile, isMobile, state } = useSidebar()
  const isCollapsed = state === "collapsed"

  const onLinkClick = () => {
    if (isMobile) setOpenMobile(false)
  }

  return (
    <>
      <SidebarHeader>
        <div className="flex shrink-0 items-center justify-start px-4 pt-6 pb-12 transition-[padding] duration-300 ease-[0.25,0.1,0.25,1] group-data-[state=collapsed]:pt-4 group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:pb-8 group-data-[state=collapsed]:px-2">
          <Link
            to={APP_ROUTES.home}
            onClick={onLinkClick}
            className="focus-visible:ring-ring flex shrink-0 items-center justify-start overflow-hidden rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 "
          >
            <motion.div
              layout
              className="relative flex items-center justify-center overflow-hidden"
              animate={{
                width: isCollapsed ? 48 : 167,
                height: isCollapsed ? 48 : 40,
              }}
              transition={logoTransition}
            >
              {/* Both logos in DOM, crossfade by opacity – no unmount = no position jump */}
              <motion.img
                src={ImgLogo}
                alt={logoAlt}
                aria-hidden={!isCollapsed}
                className="absolute inset-0 h-full w-full object-contain object-center"
                initial={false}
                animate={{
                  opacity: isCollapsed ? 1 : 0,
                  scale: isCollapsed ? 1 : 0.92,
                }}
                transition={logoTransition}
              />
              <motion.img
                src={ImgLogoWithFullName}
                alt={logoAlt}
                aria-hidden={isCollapsed}
                className="absolute inset-0 h-full w-full object-contain object-center"
                width={167}
                height={40}
                initial={false}
                animate={{
                  opacity: isCollapsed ? 0 : 1,
                  scale: isCollapsed ? 0.95 : 1,
                }}
                transition={logoTransition}
              />
            </motion.div>
          </Link>
        </div>
      </SidebarHeader>

      <div className="flex shrink-0 justify-center px-3 pb-6 transition-[padding] duration-300 ease-[0.25,0.1,0.25,1] group-data-[state=collapsed]:pb-4">
        <Link
          to={APP_ROUTES.policyCreate}
          onClick={onLinkClick}
          className="bg-primary text-primary-foreground hover:bg-primary-75 focus-visible:ring-ring flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-3 text-sm font-medium transition-[color,padding,width] duration-300 ease-[0.25,0.1,0.25,1] group-data-[state=collapsed]:p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          <span className="flex size-9 shrink-0 scale-[0.778] items-center justify-center rounded-full transition-transform duration-300 ease-in-out group-data-[state=collapsed]:scale-100">
            <HugeiconsIcon
              icon={Icons.PlusSignCircle}
              size={28}
              color="currentColor"
            />
          </span>
          <AnimatePresence initial={false}>
            {!isCollapsed && (
              <motion.span
                key="new-policy-label"
                className="app-text-title-small inline-block min-w-0 whitespace-nowrap"
                initial={{ opacity: 0, maxWidth: 0 }}
                animate={{ opacity: 1, maxWidth: 140 }}
                exit={{ opacity: 0, maxWidth: 0 }}
                transition={labelTransition}
                style={{ overflow: "hidden" }}
              >
                {intl.formatMessage({ id: "policies.newPolicy" })}
              </motion.span>
            )}
          </AnimatePresence>
        </Link>
      </div>

      <SidebarContent>
        <ScrollArea className="mt-3 flex-1">
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu className="flex flex-col space-y-1 px-1">
                {NAV_ITEMS.map((item) => {
                  const isActive = pathname === item.path
                  return (
                    <SidebarMenuItem key={item.path}>
                      <SidebarMenuButton asChild isActive={isActive}>
                        <Link
                          to={item.path}
                          onClick={onLinkClick}
                          className={cn(
                            "text-primary-text flex h-full w-full items-center gap-3 rounded-xl p-4 group-data-[state=collapsed]:min-w-14",
                            "transition-[background-color,padding,width] duration-300 ease-[0.25,0.1,0.25,1]",
                            "hover:bg-primary-25!",
                            isActive && "bg-primary-25!"
                          )}
                        >
                          <span className="flex size-7 shrink-0 scale-[0.714] items-center justify-center transition-transform duration-300 ease-in-out group-data-[state=collapsed]:scale-100">
                            <HugeiconsIcon
                              icon={item.icon}
                              size={28}
                              className="size-full"
                            />
                          </span>
                          <AnimatePresence initial={false}>
                            {!isCollapsed && (
                              <motion.span
                                key={item.path}
                                className="app-text-title-small inline-block min-w-0 whitespace-nowrap"
                                initial={{ opacity: 0, maxWidth: 0 }}
                                animate={{ opacity: 1, maxWidth: 180 }}
                                exit={{ opacity: 0, maxWidth: 0 }}
                                transition={labelTransition}
                                style={{ overflow: "hidden" }}
                              >
                                {intl.formatMessage({ id: item.labelKey })}
                              </motion.span>
                            )}
                          </AnimatePresence>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </ScrollArea>
      </SidebarContent>
    </>
  )
}

export function AppSidebar({ logoAlt }: { logoAlt: string }) {
  return (
    <ShadcnSidebar collapsible="icon" className="group bg-background">
      <AppSidebarContent logoAlt={logoAlt} />
    </ShadcnSidebar>
  )
}
